import { GoogleMap } from '@react-google-maps/api'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { connect, ConnectedProps } from 'react-redux'
import styled from 'styled-components'

import TripItemMapRouteComponent from './MapElement/TripItemMapRoute'
import TripItemMapMarkerComponent from './MapElement/TripItemMapMarker'
import ExploreCardCarousel from './MapElement/ExploreCardCarousel'
import ExploreCardSingle from './MapElement/ExploreCardSingle'
import ExploreMapMarker from './MapElement/ExploreMapMarker'
import GooglePOICardSingle from './MapElement/GooglePOICardSingle'
import GooglePOIMarker from './MapElement/GooglePOIMarker'
import SearchCardSingle from './MapElement/SearchCardSingle'
import SearchMapMarker from './MapElement/SearchMapMarker'
import TripItemCardSingle from './MapElement/TripItemCardSingle'
import MapSearch from './MapSearch/MapSearch'
import MapZoomButtons from './MapZoomButtons/MapZoomButtons'
import {
  isMapItem,
  panToIntoViewIfObstructed,
  isExploreMarker,
  isRoute,
  filterMapElements,
  findNewOrMovedTripItems,
} from './utils'
import TooltipModal from '../TripModal/TooltipModal'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useGoogleMapsContext } from 'contexts/GoogleMapsContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useResponsive } from 'hooks/useResponsive'
import { arrayToMap, EmptyArray } from 'lib/array/arrayUtils'
import { useIsTabletAndSmallerScreen } from 'hooks/useScreenSize'
import {
  DEFAULT_MAP_OPTIONS,
} from 'tripPlanner/config/map'
import { useTripExplorePlaceDetails } from 'tripPlanner/hooks/Google/useTripExplorePlaceDetail'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import {
  clearCurrentSectionId,
  setCurrentSectionId,
} from 'tripPlanner/reducers/actions'
import { ExploreItem, FullTrip, ItemWithOnlyLatLng } from 'tripPlanner/types/common'
import { PublicFullTrip, PublicMapItem } from 'tripPlanner/types/publicTrip'
import { MapItem } from 'tripPlanner/types/tripItem'
import {
  getPlaceIdToSavedExploreItemMap,
} from 'tripPlanner/utils/explore'
import { itemSectionId } from 'tripPlanner/utils/itemSection'
import { TripTab } from 'tripPlanner/utils/tabs'
import MapLayersButton from './MapLayers/MapLayersButton'
import MapLayersContext from 'tripPlanner/contexts/MapLayers/MapLayersContext'
import { TripMapContext, TripMapContextValue } from 'tripPlanner/contexts/TripMapContext'
import useExplorePins from './hooks/useExplorePins'
import useTripItemMapElements from './hooks/useTripItemMapElements'
import useAutoMapBounds from './hooks/useAutoMapBounds'
import useMapSearchState from './hooks/useMapSearchState'
import { getTripFilters } from 'tripPlanner/utils/selectors'
import { getCurrentUserId } from 'selectors/accountSelectors'
import usePrevious from 'hooks/usePrevious'
import debounce from 'lodash.debounce'
import LEExperienceMapMarker from './MapElement/LEExperienceMapMarker'
import useLEExperiencePins from './hooks/useLEExperiencePins'
import LEOfferCard from './MapElement/LEOfferCard'
import ProductLayersChips from './MapLayers/ProductLayersChips'

const containerStyle = {
  height: '100%',
  minHeight: '80vh',
}

const ModalContainer = styled.div`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const DesktopRoot = styled.div`
  height: 100%;
`

export interface CustomModalConfig {
  title?: string
  closeModalAction?: () => void
  backButtonAction?: () => void
}

interface MapProps {
  title: string
  tab: TripTab
  trip: FullTrip | PublicFullTrip
  canEdit: boolean
  mapContainerClassName?: string
  currentSectionId?: string
  /**
   * Currently only affects explore items.
   * - If true, the map will use a carousel to display explore item cards.
   * - If false, selecting an explore item will bring up a single card.
   */
  useCardCarousel: boolean
  updateModalConfig?: (value: CustomModalConfig) => void
  isTripLoading?: boolean
}

function BaseTripMap({
  trip,
  canEdit,
  tab = 'Summary',
  mapContainerClassName,
  currentSectionId,
  useCardCarousel,
  updateModalConfig,
  isTripLoading = false,
}: ConnectedProps<typeof connector>) {
  const items = useMemo(() => trip.items.filter(isMapItem) as Array<MapItem | PublicMapItem>, [trip.items])

  const tripHasDates = !!(trip.startDate && trip.endDate)
  const showItineraryAsSummary = !tripHasDates
  const filters = useAppSelector(getTripFilters)

  const tripId = trip.id
  const dispatch = useAppDispatch()

  const [POIItem, setPOIItem] = useState<
    ExploreItem | ItemWithOnlyLatLng | null | undefined
  >() // While loading, this item will only have lat and long values.

  const mapContext = useMemo<TripMapContextValue>(() => ({
    onSelectMapItem: (sectionId: string) => {
      dispatch(setCurrentSectionId(sectionId))
    },
    onDeselectMapItem: () => {
      dispatch(clearCurrentSectionId())
      setPOIItem(undefined)
    },
  }), [dispatch])

  const [isZooming, setIsZooming] = useState<boolean>(false) // nothing uses this state, but without it, flight paths don't render.
  const mapRef = useRef<google.maps.Map>()

  const { isLoaded: isMapsContextLoaded } = useGoogleMapsContext()

  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false)

  const currentUserId = useAppSelector(getCurrentUserId)!
  const prevItems = usePrevious(trip.items, trip.items)
  const newOrMovedTripItems = useMemo(() => findNewOrMovedTripItems(currentUserId, trip.items, prevItems), [currentUserId, trip.items, prevItems])

  useEffect(() => {
    if (newOrMovedTripItems.length > 0) {
      dispatch(setCurrentSectionId(itemSectionId(newOrMovedTripItems[0].id)))
    }
  }, [newOrMovedTripItems, dispatch])

  const {
    exploreItems,
    exploreCategory,
    exploreMarkers,
    exploreIsLoading,
  } = useExplorePins(tab)

  const exploreItemsToUse: Array<ExploreItem> = tab === 'Explore' ? exploreItems : EmptyArray

  const { layerGroups } = useContext(MapLayersContext)
  const layerGroupsToUse = tab !== 'Explore' ? layerGroups : EmptyArray

  const allTripItemMarkersWithoutExplorePlaces = useTripItemMapElements(trip, tab, exploreItemsToUse)

  const allTripItemAndExploreMapElements = useMemo(() => {
    return [
      ...allTripItemMarkersWithoutExplorePlaces,
      ...exploreMarkers,
    ]
  }, [allTripItemMarkersWithoutExplorePlaces, exploreMarkers])

  const filtersToUse: Array<App.TripPlannerProductFilterType> = tab === 'Itinerary' && showItineraryAsSummary ? filters : EmptyArray

  const tripItemAndExploreMapElements = useMemo(() => {
    return filterMapElements(trip, allTripItemAndExploreMapElements, layerGroups, filtersToUse)
  }, [allTripItemAndExploreMapElements, filtersToUse, layerGroups, trip])

  const [bounds, setBounds] = useState<string | undefined>()
  const onBoundsChanged = useMemo<() => void>(() => debounce(
    () => {
      const map = mapRef.current
      if (map) {
        setBounds(map.getBounds()?.toUrlValue())
      }
    },
    2000,
    { leading: true, trailing: true },
  ), [])

  const experiencesMarkers = useLEExperiencePins(trip, layerGroups, bounds)

  useAutoMapBounds(mapRef, isMapLoaded, tab, isTripLoading, exploreIsLoading, exploreItemsToUse, tripItemAndExploreMapElements, allTripItemAndExploreMapElements, newOrMovedTripItems)

  const tripItemMarkersBySectionId = useMemo(() => arrayToMap(allTripItemMarkersWithoutExplorePlaces, marker => itemSectionId(marker.item.id)), [allTripItemMarkersWithoutExplorePlaces])
  const exploreMarkersBySectionId = useMemo(() => arrayToMap(exploreMarkers, marker => itemSectionId(marker.item.id)), [exploreMarkers])
  const experiencesMarkersBySectionId = useMemo(() => arrayToMap(experiencesMarkers, marker => itemSectionId(marker.offer.id)), [experiencesMarkers])

  const activeExploreMarker = exploreMarkersBySectionId.get(currentSectionId as string)
  const activeTripItemMarker = tripItemMarkersBySectionId.get(currentSectionId as string)
  const activeExperienceMarker = experiencesMarkersBySectionId.get(currentSectionId as string)

  const savedExploreItemsByPlaceId = getPlaceIdToSavedExploreItemMap(items)

  const carouselContainerRef = useRef<HTMLDivElement>(null)
  const googlePOICardSingleRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (activeExploreMarker && mapRef.current) {
      panToIntoViewIfObstructed(
        mapRef.current,
        activeExploreMarker.position,
        carouselContainerRef.current,
      )
    }
  }, [activeExploreMarker])

  const {
    setMapSearchItem,
    mapSearchMarker,
    clearMapSearchItem,
  } = useMapSearchState(mapRef, items, exploreItems, useCardCarousel)

  const isTabletOrSmaller = useIsTabletAndSmallerScreen()

  const tripItemCardSingleRef = useRef<HTMLDivElement | null>(null)
  const leOfferCardRef = useRef<HTMLDivElement | null>(null)

  const { data: fullPoiItems } = useTripExplorePlaceDetails({
    placeIds: [POIItem?.id],
  })

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map
      setIsMapLoaded(true)
      setBounds(map.getBounds()?.toUrlValue())
      map.addListener('click', (event: google.maps.IconMouseEvent) => {
        if (!event.placeId) {
          return
        }

        if (event.latLng) {
          // realistically, if they are clicking a point on the map...
          // it will have a lat and lng value.
          setPOIItem({
            id: event.placeId,
            place: { point: event.latLng?.toJSON() },
          })
        }

        dispatch(setCurrentSectionId(itemSectionId(event.placeId)))
        event.stop()
      })
    },
    [dispatch],
  )

  const showLayersButton = (!useCardCarousel || !activeExploreMarker) && layerGroupsToUse.length > 0 && tab !== 'Explore'

  return (
    <TripMapContext.Provider value={mapContext}>
      {isMapsContextLoaded && (
        <GoogleMap
          options={DEFAULT_MAP_OPTIONS}
          onLoad={onLoad}
          onIdle={() => setIsZooming(false)}
          onZoomChanged={() => setIsZooming(true)}
          mapContainerStyle={containerStyle}
          mapContainerClassName={mapContainerClassName}
          onBoundsChanged={onBoundsChanged}
        >
          <MapSearch
            mapRef={mapRef}
            tripId={tripId}
            updateModalConfig={updateModalConfig}
            setMapSearchItem={setMapSearchItem}
          />
          <ProductLayersChips />
          {!isTabletOrSmaller && <MapZoomButtons mapRef={mapRef} />}

          {tripItemAndExploreMapElements.map((marker, i) => {
            const itemId = itemSectionId(marker.item.id)
            if (isRoute(marker)) {
              return (
                <TripItemMapRouteComponent
                  zIndex={i}
                  key={`route-${i}`}
                  item={marker.item}
                  position={marker.position}
                  active={itemId === currentSectionId}
                  map={mapRef.current}
                  isZooming={isZooming}
                />
              )
            } else if (isExploreMarker(marker)) {
              return (
                <ExploreMapMarker
                  zIndex={i}
                  key={`marker-${marker.item.id}`}
                  category={exploreCategory}
                  item={marker.item}
                  position={marker.position}
                  active={itemId === currentSectionId}
                  saved={savedExploreItemsByPlaceId.has(marker.item.place.id as string)}
                />
              )
            } else {
              const markerPlace = 'place' in marker.item ? marker.item.place : marker.item.startPlace
              return (
                markerPlace &&
                mapSearchMarker?.item.id !== markerPlace.id && ( // hidden if its the same as the current search marker
                  <TripItemMapMarkerComponent
                    itemCardRef={tripItemCardSingleRef}
                    zIndex={i}
                    key={`marker-${marker.item.id}-${marker.itemPlaceType}`}
                    item={marker.item}
                    position={marker.position}
                    active={itemId === currentSectionId}
                    tab={tab}
                  />
                )
              )
            }
          })}

          {experiencesMarkers.map((marker, i) => (
            <LEExperienceMapMarker
              zIndex={tripItemAndExploreMapElements.length + i}
              key={`marker-${marker.offer.id}`}
              experience={marker.offer}
              position={marker.position}
              active={itemSectionId(marker.offer.id) === currentSectionId}
              itemCardRef={leOfferCardRef}
            />
          ))}

          {mapSearchMarker && (
            <SearchMapMarker
              zIndex={tripItemAndExploreMapElements.length + experiencesMarkers.length + 1}
              key={`marker-${mapSearchMarker.item.id}`}
              item={mapSearchMarker.item}
              position={mapSearchMarker.position}
              active={
                itemSectionId(mapSearchMarker.item.id) === currentSectionId
              }
              saved={savedExploreItemsByPlaceId.has(
                mapSearchMarker.item.place.id!,
              )}
            />
          )}
          {POIItem && (
            <GooglePOIMarker
              itemCardRef={googlePOICardSingleRef}
              active={itemSectionId(POIItem.id) === currentSectionId}
              zIndex={tripItemAndExploreMapElements.length + experiencesMarkers.length + 2}
              item={POIItem}
              key={`marker-${POIItem.id}`}
              position={POIItem.place.point}
            />
          )}
          {showLayersButton && <MapLayersButton />}
          {!activeExploreMarker &&
            !activeTripItemMarker &&
            mapSearchMarker &&
            itemSectionId(mapSearchMarker.item.id) === currentSectionId &&
            currentSectionId && ( // its a searched for marker that is active.
              <SearchCardSingle
                onSave={clearMapSearchItem}
                selectedMarker={mapSearchMarker}
                containerRef={carouselContainerRef}
              />
          )}
          {!activeExploreMarker &&
            !activeTripItemMarker &&
            POIItem &&
            itemSectionId(POIItem.id) === currentSectionId &&
            currentSectionId && ( // its a POI marker that is active.
              <GooglePOICardSingle
                selectedMarker={fullPoiItems?.[0] || POIItem}
                containerRef={googlePOICardSingleRef}
              />
          )}
          {!useCardCarousel && activeExploreMarker && (
            <ExploreCardSingle
              selectedMarker={activeExploreMarker}
              containerRef={carouselContainerRef}
            />
          )}
          {useCardCarousel && (
            <ExploreCardCarousel
              exploreItems={exploreItems}
              selectedMarker={activeExploreMarker}
              containerRef={carouselContainerRef}
            />
          )}
          {activeTripItemMarker && (
            <TripItemCardSingle
              cardRef={tripItemCardSingleRef}
              selectedItem={activeTripItemMarker.item}
              containerRef={carouselContainerRef}
            />
          )}
          {activeExperienceMarker && (
            <LEOfferCard
              offer={activeExperienceMarker.offer}
              cardRef={leOfferCardRef}
            />
          )}
        </GoogleMap>
      )}
      {!isMapsContextLoaded && <LoadingIndicator floating />}
      {isMapsContextLoaded && items.length === 0 && canEdit && (
        <ModalContainer>
          <TooltipModal tripId={tripId} />
        </ModalContainer>
      )}
    </TripMapContext.Provider>
  )
}

const mapStateToProps = (state: App.State, ownProps: MapProps) => ({
  currentSectionId: state.tripPlanner.currentSectionId,
  ...ownProps,
})

const connector = connect(mapStateToProps)
export const TripMap = connector(BaseTripMap)

export function DesktopTripMap(props: MapProps) {
  return (
    <DesktopRoot>
      <TripMap {...props} />
    </DesktopRoot>
  )
}

// We omit items here and instead get it in the actual component itself since this is
// used in a modal. Modals can only handle static data. Not dynamic data such as items
type MobileMapProps = Omit<MapProps, 'items'>

export function MobileTripMap(props: MobileMapProps) {
  const { closeModal, goBack } = useModalCallbacks()

  const [match] = useResponsive()

  useEffect(() => {
    if (match.desktop || match.largeDesktop) {
      closeModal()
    }
  }, [match, closeModal])

  // customModalConfig when set will override the default modal attributes
  // when unset, modalAttributes will be used instead.
  const [customModalConfig, setCustomModalConfig] = useState<CustomModalConfig>(
    {
      title: undefined,
      closeModalAction: undefined,
      backButtonAction: undefined,
    },
  )

  const [modalAttributes, setModalAttributes] = useState({
    title: props.title,
    closeModalAction: closeModal,
    backButtonAction: goBack,
  })

  useEffect(() => {
    setModalAttributes({
      title: customModalConfig.title ?? props.title,
      closeModalAction: customModalConfig.closeModalAction ?? closeModal,
      backButtonAction: customModalConfig.backButtonAction ?? goBack,
    })
  }, [closeModal, customModalConfig, goBack, props.title])

  return (
    <>
      <ModalHeader
        title={modalAttributes.title}
        onCloseButtonClick={modalAttributes.closeModalAction}
        onBackButtonClick={modalAttributes.backButtonAction}
      />
      <TripMap
        {...props}
        updateModalConfig={setCustomModalConfig}
      />
    </>
  )
}
